import { updateUserProfile } from "@/services/profileService";
import { platformApiFetch } from '@/services/platformApi/client';
import { queryClient } from '@/lib/react-query';
import { clearBrowserAuthState, clearTransientBrowserState } from '@/utils/authStorage';
import { toast } from "@/hooks/use-toast";
import { getCurrentUserId } from "./getCurrentUserId";

/**
 * Delete user account
 * @param userId User ID
 */
export const deleteUserAccount = async (userId: string): Promise<void> => {
  try {
    await platformApiFetch('/v1/account/delete', {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });

    // Only sign out if the current user is deleting their own account
    const currentUserId = await getCurrentUserId();
    if (currentUserId === userId) {
      queryClient.clear();
      clearBrowserAuthState();
      clearTransientBrowserState();

      await platformApiFetch('/v1/auth/sign-out', {
        method: 'POST',
      }).catch(() => undefined);
    }
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to delete account';
    toast({
      title: "Error deleting account",
      description: message,
      variant: "destructive",
    });
    throw error;
  }
};

/**
 * Toggle multi-factor authentication
 * @param userId User ID 
 * @param enable Whether to enable or disable MFA
 */
export const toggleUserMfa = async (userId: string, enable: boolean): Promise<void> => {
  try {
    // This is a placeholder - actual MFA implementation would need to use Supabase's MFA APIs
    // which might require additional steps based on your requirements
    
    // For a simple example, we'll update a user's profile to indicate MFA preference
    await updateUserProfile(userId, { mfa_enabled: enable });
    
    toast({
      title: enable ? "MFA Enabled" : "MFA Disabled",
      description: enable 
        ? "Multi-factor authentication has been enabled for your account." 
        : "Multi-factor authentication has been disabled for your account.",
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : 'Failed to update MFA settings';
    toast({
      title: "Error updating MFA settings",
      description: message,
      variant: "destructive",
    });
    throw error;
  }
};
